import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pageStyles from './BranchDetail.module.css';
import tableStyles from '../components/branches/SectionTable.module.css';
import SectionTable from '../components/branches/SectionTable';
import ListPageHeader from '../components/common/ListPageHeader';
import Ico from '../components/common/Ico';
import Pill from '../components/common/Pill';
import ActionsForBranch from '../components/branches/ActionsForBranch';
import NewBranchDialog from '../components/dialog/NewBranchDialog';
import { apiGetBranchesList } from '../api/branches';
import { statusLabel } from '../components/branches/statusConfig';
import { timeSinceTS } from '../helpers/dates';
import { WebSocketContext } from '../api/socket';

const ICO_BRANCH = 'M6 3v12 M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 9a9 9 0 0 1-9 9';
const ICO_CHECK  = 'M20 6 9 17l-5-5';
const ICO_PLUS   = 'M12 5v14M5 12h14';

const COLUMNS = [
  { label: 'Version',       width: '18%' },
  { label: 'Status',        width: '14%' },
  { label: 'Test Instance', width: '19%' },
  { label: 'Nightly',       width: '13%' },
  { label: 'Created',       width: '14%' },
  { label: 'Actions',       width: '22%' },
];

function renderRow(branch, reloadData) {
  return (
    <>
      <td className={tableStyles.td}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Ico d={ICO_BRANCH} size={14} style={{ color: 'var(--fg-muted)' }} />
          <span className={tableStyles.mono}>{branch.version}</span>
        </span>
      </td>
      <td className={tableStyles.td}>
        <Pill status={branch.status} dot pulse={branch.status === 'branching'}>
          {statusLabel(branch.status)}
        </Pill>
      </td>
      <td className={tableStyles.td}>
        <span className={tableStyles.mono} style={{ fontSize: 12.5 }}>{branch.test_instance?.url || '—'}</span>
      </td>
      <td className={tableStyles.td}>
        {branch.nightly_enabled ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--fg-secondary)' }}>
            <Ico d={ICO_CHECK} size={12} strokeWidth={2} style={{ color: 'var(--success-500)' }} />
            Yes
          </span>
        ) : (
          <span className={tableStyles.muted}>No</span>
        )}
      </td>
      <td className={tableStyles.td}>{timeSinceTS(branch.created)}</td>
      <td className={tableStyles.td} onClick={(e) => e.stopPropagation()}>
        <ActionsForBranch branch={branch} reloadData={reloadData} size="sm" />
      </td>
    </>
  );
}

export default function BranchesList({ user }) {
  const navigate = useNavigate();
  const ws = useContext(WebSocketContext);
  const [branches, setBranches] = useState({ loaded: false, list: [] });
  const [newBranchOpen, setNewBranchOpen] = useState(false);

  const reloadData = () => {
    apiGetBranchesList().then((r) => {
      const sorted = [...r.branches].sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }));
      setBranches({ loaded: true, list: sorted });
    }).catch((e) => {
      setBranches({ loaded: true, list: [], error: e });
    });
  };

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!ws) return;
    ws.on('branch_update', reloadData);
    return () => { ws.off('branch_update'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  if (!branches.loaded) {
    return (
      <div className={pageStyles.loading}>
        <div className={pageStyles.loadingSpinner} />
        Loading branches…
      </div>
    );
  }

  const activeCount = branches.list.filter((b) => b.status === 'active').length;

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.inner}>
        <ListPageHeader
          title="Branches"
          subtitle={`${branches.list.length} branch${branches.list.length === 1 ? '' : 'es'} · ${activeCount} active`}
          onReload={reloadData}
        />
        <SectionTable
          id="branches-table"
          icon={ICO_BRANCH}
          title="All branches"
          searchPlaceholder="Search branches…"
          searchFilter={(b, q) => b.version.toLowerCase().includes(q.toLowerCase())}
          triggerLabel="Create new"
          triggerIcon={ICO_PLUS}
          onTrigger={() => setNewBranchOpen(true)}
          items={branches.list}
          columns={COLUMNS}
          renderRow={(b) => renderRow(b, reloadData)}
          getKey={(b) => b.version}
          onOpenItem={(b) => navigate(`/branches/${b.version}`)}
          pageSize={15}
          emptyTitle="No branches yet"
          emptyHint="Create a branch to get started."
        />
      </div>
      <NewBranchDialog
        open={newBranchOpen}
        user={user}
        onClose={() => { setNewBranchOpen(false); reloadData(); }}
      />
    </div>
  );
}
